/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-06-27 08:54:31
 **/
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback, useEffect, useRef } from "react";
import { IFrame, StompConfig } from "@stomp/stompjs";
import { timer, Subscription, BehaviorSubject, fromEvent, combineLatest } from "rxjs";

import { useStompClient, useStompManager } from "@/frameworks/libraries/network";
import { EventHandler, useEventHandler, useEventHandlers } from "@rfice/eventhandler";
import { Properties } from "@/frameworks/libraries/properties";
import { Configure, Environments } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { useToast } from "@/frameworks/designStuff/components";
import { SystemError, Topics, WebsocketErrorCode } from "@/frameworks/core/commons";
import { useChatServiceManager } from "@/frameworks/core/chat";
import { XmsNetwork } from "@/frameworks/core/engine";
import useOnlineMonitor from "./useOnlineMonitor";
import { usePeopleServiceManager } from "../../usePeopleServiceManager";
import { useAccountActions } from "@/frameworks/core/hooks";
import { usePersistActions, ChatActions, UserActions, SpaceActions, UnityActions } from "@/frameworks/core/persistence";
import check_or_refresh from "@/routes/utils";
import WSNetworkServiceObserver from "./WSNetworkObserver";

const LOGTAG = "#useNetworkManager#";

export type NetworkInitialize = {
	connect_stomp_server: () => Promise<void>;
};

export default function useNetworkManager(): NetworkInitialize {
	const reconnectTimerRef = useRef<Subscription | null>(null);
	const reconnectDelayRef = useRef<number>(0);
	const myOldActiveStatus = useRef<WebServices.User.UserInfo["activeStatus"]>(undefined);
	const toast = useToast();
	const chatManager = useChatServiceManager();
	const peopleManager = usePeopleServiceManager();
	const stompNetwork = useStompManager();
	const stateOnline = useOnlineMonitor();
	const { logout } = useAccountActions();
	const dispatch = usePersistActions();

	const os_network_observer$ = useRef<BehaviorSubject<boolean>>(new BehaviorSubject<boolean>(stateOnline));
	const stomp_network_observer$ = useRef<BehaviorSubject<"idle" | "fullfiled" | "pending" | "rejected">>(
		WSNetworkServiceObserver.getInstance().status$
	);
	const service_network_observer$ = useRef<BehaviorSubject<"idle" | "fullfiled" | "pending" | "rejected">>(
		new BehaviorSubject<"idle" | "fullfiled" | "pending" | "rejected">(
			serviceNetworkPredicate(os_network_observer$.current.value, stomp_network_observer$.current.value)
		)
	);

	const networkConnectOption = useCallback((domain: URL) => {
		//@ts-ignore
		window.tabID = Properties.browserTabID;
		return {
			brokerURL: "wss://" + domain.host + domain.pathname,
			connectHeaders: {
				authorization: Configure.authorizationKey,
				"device-type": "WEB",
				"device-key": Properties.browserTabID,
				"app-version": Environments.env("ROOT_VERSION") as string
			},
			reconnectDelay: 0, // 5000,
			heartbeatIncoming: 2000,
			heartbeatOutgoing: 2000,
			heartbeatStrategy: "worker", // 7.0.0에서는 비표준
			debug: (msg: string) => {
				Configure.useLogLevel >= 7 && console.debug(LOGTAG, "Network Debug:", msg);
			}
		} as StompConfig;
	}, []);

	const _connect_server = async () => {
		// console.log("hsshin, _connect_server", Properties.property.webSocketURL, stompNetwork.isConnected);
		if (Properties.property.webSocketURL && !stompNetwork.isConnected) {
			// console.groupCollapsed(LOGTAG, "Connect STOMP Server");
			console.debug("#Option#", networkConnectOption(Properties.property.webSocketURL));
			stompNetwork.clear();
			await stompNetwork.connect(networkConnectOption(Properties.property.webSocketURL));
			// console.groupEnd();
		}
	};

	const connect_stomp_server = async () => {
		// console.log("hsshin, connect_stomp_server", reconnectDelayRef.current);
		if (reconnectDelayRef.current === 0) {
			reconnectDelayRef.current = 1000;
			await _connect_server();
		} else {
			reconnectDelayRef.current = reconnectDelayRef.current * 2; // 재접속 실패 시 두배씩 지연시간 증량
			if (reconnectDelayRef.current > 30 * 1000) {
				reconnectDelayRef.current = 1000;
			}
			reconnectTimerRef.current = timer(reconnectDelayRef.current).subscribe(async () => {
				await _connect_server();
			});
		}
	};

	const go_to_login_page = () => {
		if (location.pathname.includes("/admin")) {
			window.location.replace("/login/admin");
		} else {
			window.location.replace("/");
		}
	};

	useStompClient({
		onConnected(arg: IFrame) {
			EventHandler.fire("$Network::Subscribing$", arg);
		},
		onConnectFailure() {
			// console.debug(LOGTAG, "STOMP onConnectFailure", arg);
		},
		onDisconnect(arg) {
			console.debug(LOGTAG, "STOMP onDisconnect", arg);
		},
		onSubscribe(arg) {
			console.debug(LOGTAG, "STOMP onSubscribe", arg);
		},
		onUnsubscribe(arg) {
			console.debug(LOGTAG, "STOMP onUnsubscribe", arg);
		},
		async onStompError(arg) {
			const body = JSON.parse(arg.body) as { errorCode: WebsocketErrorCode; message: string };
			if (body) {
				switch (body.errorCode) {
					case WebsocketErrorCode.JWT_EXPIRED: {
						console.warn(LOGTAG, "Token expired");

						// JWT 토큰 에러가 발생하였을 경우, 인증이 필요한 [*]REST_API 호출후에 reissue_token 프로세스 진행
						check_or_refresh().catch((error: unknown) => {
							Configure.clear_authenticated();
							throw error;
						});
						break;
					}

					case WebsocketErrorCode.JWT_VERIFICATION_FAILED: {
						console.warn(LOGTAG, "Token verification failed");
						Configure.clear_authenticated();
						go_to_login_page();
						break;
					}

					default: {
						console.warn(LOGTAG, "Stomp error with", body.errorCode, body.message);
						break;
					}
				}
			} else {
				console.warn(LOGTAG, "Stomp error", arg);
			}
		},
		onMessageReceived(message, topic: string) {
			{
				const messageType = message.type as ChatServices.MessageIDs;
				switch (messageType) {
					case "deviceMediaState":
					case "voiceChatJoined":
					case "voiceChatLeft":
					case "voiceChatStatusUpdated":
					case "voiceChatRecordStatusUpdated": {
						EventHandler.fire("$VoiceCall::Message$", messageType, message, topic);
						return;
					}
				}
			}

			{
				const messageType = message.type as PushServices.Account.MessageIDs;
				switch (messageType) {
					case "disconnectedByOtherDevice": {
						const { deviceType, deviceKey } =
							message.payload as PushServices.Account.DisconnectedByOtherDevice.Data;

						if (deviceType === "WEB" && deviceKey !== Properties.browserTabID) {
							console.debug(LOGTAG, "Disconnected by other device", deviceKey, Properties.browserTabID);
							Configure.disconnectedByOtherDevice = true;
							logout();
						}
						return;
					}
				}
			}

			XmsNetwork.on_message_received(message, topic);

			UserActions.instance.on_message_received(message, topic);
			ChatActions.instance.on_message_received(message, topic);
			UnityActions.instance.on_message_received(message, topic);
			SpaceActions.instance.on_message_received(message, topic);
			// XMS.NetworkManager.on_message_received(topic, message);
		},
		onWebSocketClosed(message: IFrame) {
			console.debug(LOGTAG, "STOMP onWebSocketClosed", message);
			if (!Configure.is_authenticated) {
				go_to_login_page();
			}
		}
	});

	const show_disconnected_toast = () => {
		new SystemError(_T("네트워크 연결 상태가 좋지 않습니다. 잠시 후 재시도합니다."), {
			errorType: "fatal",
			showToast: true,
			cause: {
				errorCode: "80000",
				errorMessage: _T("네트워크 연결 상태가 좋지 않습니다. 잠시 후 재시도합니다.")
			}
		});
	};

	const hide_disconnected_toast = () => {
		toast.hide("80000");
	};

	useEventHandlers({
		async onNetworkSubscribing() {
			//*
			/*/
			XMS.NetworkManager.config = {
				topics: Topics,
				media_server_host_name: Properties.mediaServerHostName,
				iceServers: Properties.iceServers,
				subscribe(topics: AllTopics, addTopics?: boolean, callback?: (() => void) | null) {
					stompNetwork.subscribe(topics, addTopics, callback);
				},
				unsubscribe(topics: AllTopics) {
					stompNetwork.unsubscribe(topics);
				},
				publish<T>(topics: Topic, type: any, payload: T, options: any) {
					stompNetwork.publish(topics, type, payload, options);
				}
			};
			//*/

			try {
				if (Configure.userID) {
					stompNetwork.subscribe(Topics.Peer(Configure.userID));
				} else {
					throw Error("User ID is not defined");
				}
				dispatch.chats.load((topics) => topics.length > 0 && stompNetwork.subscribe(topics));
				peopleManager.run();
				chatManager.run();
			} catch (error) {
				console.debug(LOGTAG, "Subscribing failed with", error);
			}
		}
	});

	useEventHandler("authorization_changed", async (type: string, value: string | null) => {
		switch (type) {
			case "authorization_key": {
				if (value) {
					await connect_stomp_server();
				} else {
					window.location.replace("/");
				}
				break;
			}

			case "cleaned": {
				console.debug(LOGTAG, "Authorization cleaned", location.pathname);
				break;
			}
		}
	});

	// 웹소켓 연결 상태 변경 이벤트 관찰
	useEffect(() => {
		fromEvent(stompNetwork, "STOMP_onWebSocketClosed").subscribe(() => {
			//try to reconnect;
			connect_stomp_server();
		});
	}, []);

	// os 네트워크 상태 변경 이벤트 관찰
	useEffect(() => {
		fromEvent(window, "online").subscribe(() => {
			os_network_observer$.current.next(true);
		});

		fromEvent(window, "offline").subscribe(() => {
			os_network_observer$.current.next(false);
		});
	}, []);

	//os 네트워크와, stomp 네트워크의 상태를 모두 관찰하여 모두 연결되어있는 경우에만 서비스 네트워크 연결 상태를 업데이트 함
	useEffect(() => {
		combineLatest([os_network_observer$.current, stomp_network_observer$.current]).subscribe(([os, stomp]) => {
			service_network_observer$.current.next(serviceNetworkPredicate(os, stomp));
		});
	}, []);

	// 서비스 네트워크 연결 상태를 관찰하여 서비스 로직 처리
	useEffect(() => {
		service_network_observer$.current.subscribe((value) => {
			switch (value) {
				case "fullfiled": {
					hide_disconnected_toast();
					// 서버에서 다룬 사용자 오프라인값이랑 비동기 setter로 이뤄짐으로 delay를 통해 연결 최신 상태 보장
					timer(1000).subscribe(() => {
						dispatch.users.set_user(Configure.userID, {
							activeStatus: myOldActiveStatus.current ?? "ACTIVE"
						});
					});
					break;
				}
				case "idle":
				case "pending": {
					break;
				}
				case "rejected": {
					show_disconnected_toast();
					if (dispatch.users.get_user(Configure.userID)?.activeStatus !== "OFFLINE") {
						myOldActiveStatus.current = dispatch.users.me?.activeStatus || "ACTIVE";
					}
					dispatch.users.set_user(Configure.userID, { activeStatus: "OFFLINE" });
					break;
				}
			}
		});
	}, []);

	return { connect_stomp_server };
}

function serviceNetworkPredicate(os: boolean, stomp: "idle" | "fullfiled" | "pending" | "rejected") {
	if (os && stomp === "fullfiled") {
		return "fullfiled";
	} else if (os && stomp === "pending") {
		return "pending";
	} else if (os && stomp === "idle") {
		return "idle";
	} else if (!os || stomp === "rejected") {
		return "rejected";
	} else {
		throw new Error("Invalid network status");
	}
}
